import { useState, useMemo, useRef } from "react";
import { useTranslate } from "@refinedev/core";

import { usePaginatedTable, useQueryParams } from "@shared/hooks";
import { ModalRef } from "@shared/components";
import { Plan } from "@modules/plans/types";
import { useFetchResourcesByIdsQuery } from "@shared/services/queries";
import { ApiResponse } from "@shared/types";
import { Agreement } from "@modules/agreements/types";

export function usePlanList({ agreement }: { agreement?: string } = {}) {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const createPlanModalRef = useRef<ModalRef>(null);
  const editPlanModalRef = useRef<ModalRef>(null);

  const translate = useTranslate();
  const { getQueryParams } = useQueryParams();

  const { rows, loading, refetch, ...tableProps } = usePaginatedTable<Plan>({
    resource: "v1/subscription_plan_admin",
    filters: {
      agreement: agreement,
      ...getQueryParams()
    }
  });

  const agreementsIds = useMemo(
    () => [
      ...new Set(
        rows?.map((plan) => plan.agreement).filter((id): id is number => !!id)
      ),
    ],
    [rows]
  );

  const { data: agreements } = useFetchResourcesByIdsQuery<
    ApiResponse<Agreement>
  >({
    resource: "v1/agreement_admin",
    ids: agreementsIds,
  });

  const dataToExport = useMemo(() => {
    return selectedRows.length > 0
      ? rows.filter((row) => selectedRows.includes(row.id))
      : rows;
  }, [selectedRows, rows]);

  return {
    rows,
    loading,
    tableProps,
    selectedRows,
    dataToExport,
    createPlanModalRef,
    editPlanModalRef,
    agreements: agreements?.results,
    refetch,
    setSelectedRows,
    translate,
  };
}
